import "./Cart.scss";
import  { useState, useEffect } from "react";
import CartProducts from "./../../components/CartProducts/CartProducts";
import CartSummary from "./../../components/CartSummary/CartSummary";
import { useCart } from "../../context/CartContext";
import api from "../../utils/api";
import { toast } from "react-toastify";

import { useLanguage } from "../../context/LanguageContext";

const Cart = () => {
  const {
    selectedServices,
    selectedUtilityItems,
    packages,
    appointmentDetails,
    removeItem,
    clearCart,
    selectedAddress,
    calculateTotalPrice,
    hasItems,
    promoCode,
    notes,
    minimumPrice,
    isPaymentAllowed,
    getMinimumPriceError,
  } = useCart();
  const { t, language } = useLanguage();

  // Add selectedPayment state to Cart component
  const [selectedPayment, setSelectedPayment] = useState("default"); // "default" or "tamara"

  const [paymentStatus, setPaymentStatus] = useState({
    loading: false,
    error: null,
    success: false,
    response: null,
  });

  // Get prices from the context
  const { servicesPrice, packagesPrice, totalPrice } = calculateTotalPrice();

  // All cart items combined - now from context
  const allCartItems = [...selectedServices, ...packages];

  // Effect to handle redirection when payment link is available
  useEffect(() => {
    if (
      paymentStatus.success &&
      paymentStatus.response &&
      paymentStatus.response.link
    ) {
      window.location.href = paymentStatus.response.link;
    }
  }, [paymentStatus]);





  
  // Handle service booking payment
  const handleServicePayment = async () => {
    if (!selectedServices.length) {
      toast.error("No services selected for booking");
      return false;
    }

    if (!appointmentDetails) {
      toast.error("Please select appointment date and time");
      return false;
    }

    if (!selectedAddress) {
      toast.error("Please select a delivery address");
      return false;
    }

 

    // Check minimum price requirement using CartContext
    if (!isPaymentAllowed()) {
      const errorMessage = getMinimumPriceError(language);
      if (errorMessage) {
        toast.error(errorMessage);
      }
      return false;
    }

    try {
      setPaymentStatus({ ...paymentStatus, loading: true, error: null });

      // Get the category ID from the first service
      const categoryId = selectedServices[0].category;


      // Format services with service ID and quantity as objects
      const servicesWithQuantities = selectedServices.map((service) => ({
        service: service._id,
        quantity: service.quantity || 1,
      }));

      // Format the date with hyphens instead of slashes
      const formattedDate = appointmentDetails.date.replace(/\//g, "-");

      // Convert time from Arabic AM/PM format to 24-hour format
      let formattedTime = appointmentDetails.time;

      // First check if it contains Arabic PM indicator
      if (formattedTime.includes("مساءً")) {
        // Extract the hour
        const timeParts = formattedTime.replace(" مساءً", "").split(":");
        let hour = parseInt(timeParts[0]);

        // Add 12 for PM times, except for 12 PM
        if (hour < 12) {
          hour += 12;
        }

        formattedTime = `${hour}:${timeParts[1]}`;
      }
      // Check if it contains Arabic AM indicator
      else if (formattedTime.includes("صباحًا")) {
        // Extract the hour
        const timeParts = formattedTime.replace(" صباحًا", "").split(":");
        let hour = parseInt(timeParts[0]);

        // Convert 12 AM to 00
        if (hour === 12) {
          hour = 0;
        }

        // Ensure the hour is two digits
        const formattedHour = hour.toString().padStart(2, "0");
        formattedTime = `${formattedHour}:${timeParts[1]}`;
      }

      // Prepare the request payload
      const payload = {
        category: categoryId,
        services: servicesWithQuantities, // Array of objects: [{ service: "id", quantity: number }]
        date: formattedDate,
        startTime: formattedTime,
        notes: notes || "", // Include notes (empty string if no notes)
        address: {
          name: selectedAddress.name,
          city: selectedAddress.city,
          region: selectedAddress.region,
          street: selectedAddress.street,
          buildingNo: selectedAddress.buildingNo,
          floorNo: selectedAddress.floorNo,
          addressSign: selectedAddress.addressSign || "",
          coordinates: selectedAddress.coordinates.coordinates || [],
          governorate: selectedAddress.governorate || "",
                   area:selectedAddress.area || "",
        },
      };

      // Add promo code if available
      if (promoCode && promoCode.code) {
        payload.promoCode = promoCode.code;
      }

      // Add paymentType if Tamara is selected
      if (selectedPayment === "tamara") {
        payload.paymentType = "tamara";
      }

      // Call the API endpoint
      const response = await api.post("/bookings/verify-checkout", payload);
      setPaymentStatus({
        loading: false,
        error: null,
        success: true,
        response: response.data,
      });

      return true;
      // Redirection will happen in useEffect when response.link is available
    } catch (error) {
      const errorMsg =
        error.response?.data?.message || "Failed to process payment";
      setPaymentStatus({
        loading: false,
        error: errorMsg,
        success: false,
        response: null,
      });
      toast.error(errorMsg);
      return false;
    }
  };

  


  // Handle package subscription payment
  const handlePackagePayment = async () => {
    if (!packages.length) {
      toast.error("No packages selected for subscription");
      return false;
    }

    if (!selectedAddress) {
      toast.error(t("no_address"));
      return false;
    }



    try {
      setPaymentStatus({ ...paymentStatus, loading: true, error: null });

      // We'll process only the first package for now
      // In a real scenario, you might want to handle multiple packages differently
      const packageId = packages[0]._id;

      // Prepare the request payload - DON'T INCLUDE NOTES FOR PACKAGES
      const payload = {
        packageId: packageId,
        // notes: notes || "", // REMOVED - Don't send notes for packages
        address: {
          name: selectedAddress.name,
          city: selectedAddress.city,
          region: selectedAddress.region,
          street: selectedAddress.street,
          buildingNo: selectedAddress.buildingNo,
          floorNo: selectedAddress.floorNo,
          coordinates: selectedAddress.coordinates.coordinates || [],
          addressSign: selectedAddress.addressSign || "",
          governorate: selectedAddress.governorate || "",
          area:selectedAddress.area || "",
        },
      };

      // Add promo code if available
      if (promoCode && promoCode.code) {
        payload.promoCode = promoCode.code;
      }

      // Add paymentType if Tamara is selected
      if (selectedPayment === "tamara") {
        payload.paymentType = "tamara";
      }

      // Call the API endpoint
      const response = await api.post("/subscriptions/verify", payload);

      setPaymentStatus({
        loading: false,
        error: null,
        success: true,
        response: response.data,
      });

      return true;
      // Redirection will happen in useEffect when response.link is available
    } catch (error) {
      console.error("Error processing package payment:", error);
      const errorMsg =
        error.response?.data?.message || "Failed to process payment";
      setPaymentStatus({
        loading: false,
        error: errorMsg,
        success: false,
        response: null,
      });
      toast.error(errorMsg);
      return false;
    }
  };

  // Main handler that determines what type of payment to process
  const handleConfirmOrder = async () => {
    // First check if there are items
    if (!hasItems) {
      toast.error("Your cart is empty");
      return;
    }

    // Validate basic requirements
    if (!selectedAddress) {
      toast.error(t("no_address"));
      return;
    }

 

    // If there are services, we need appointment details
    if (selectedServices.length > 0 && !appointmentDetails) {
      toast.error("Please select appointment date and time");
      return;
    }

    // Check minimum price requirement for services using CartContext
    if (selectedServices.length > 0 && !isPaymentAllowed()) {
      const errorMessage = getMinimumPriceError(language);
      if (errorMessage) {
        toast.error(errorMessage);
      }
      return;
    }

    // Determine what type of payment to process
    if (selectedServices.length > 0) {
      await handleServicePayment();
    } else if (packages.length > 0) {
      await handlePackagePayment();
    } else {
      toast.error("Your cart is empty");
    }
  };

  return (
    <div className="cart">
      <div className="container">
        <div className="cart-container">
          <CartProducts
            cartItems={allCartItems}
            services={selectedServices}
            packages={packages}
            appointmentDetails={appointmentDetails}
              utilityItems={selectedUtilityItems} // ✅ Add this prop

            onRemoveItem={removeItem} // Use context function
            onClearCart={clearCart} // Use context function
            notes={notes} // Pass notes to CartProducts
          />

          {/* Only render CartSummary if there are items in the cart */}
          {hasItems && (
            <CartSummary
              servicesPrice={servicesPrice}
              packagesPrice={packagesPrice}
              totalPrice={totalPrice}
              visitFee={0} // Set to 0 as we're not counting it
              taxAmount={0} // Set to 0 as we're not counting tax
              appointmentDetails={appointmentDetails}
              appointmentRequired={selectedServices.length > 0}
              onConfirmOrder={handleConfirmOrder}
              loading={paymentStatus.loading}
              error={paymentStatus.error}
              promoCode={promoCode}
              notes={notes} // Pass notes to CartSummary
              // Pass payment selection props
              selectedPayment={selectedPayment}
              onPaymentChange={setSelectedPayment}
              // Pass minimum price validation props from CartContext
              minimumPrice={minimumPrice}
              isPaymentAllowed={isPaymentAllowed()}
              minimumPriceError={getMinimumPriceError(language)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Cart;
